/**
 * Pure utility functions for creating and managing Three.js materials.
 *
 * These functions have no React dependencies and can be called from anywhere.
 * They centralise material creation so there is exactly one place to look
 * when debugging "why does my material look wrong".
 *
 * Supports both MeshStandardMaterial and MeshPhysicalMaterial. Physical
 * material is used when per-object physical properties (clearcoat, sheen,
 * iridescence, transmission) are set or when the scene-level setting
 * `usePhysicalMaterial` is enabled.
 */

import * as THREE from 'three';
import type { MaterialSettings } from '@/contexts/SceneSettingsContext';
import type { MaterialProperties } from '@/worker/types';

/**
 * Check whether any per-object property requires MeshPhysicalMaterial.
 */
export function needsPhysicalMaterial(
  perObj?: MaterialProperties,
  settings?: MaterialSettings,
): boolean {
  if (settings?.usePhysicalMaterial) return true;
  if (!perObj) return false;
  return (
    perObj.clearcoat > 0 ||
    perObj.sheen > 0 ||
    perObj.iridescence > 0 ||
    perObj.transmission > 0
  );
}

/**
 * Create a material with the given colour and scene-level settings.
 * Optionally accepts per-object overrides (from OpenSCAD material() calls).
 *
 * Returns MeshPhysicalMaterial when physical properties are in use,
 * otherwise MeshStandardMaterial.
 */
export function createMaterial(
  color: THREE.ColorRepresentation,
  settings: MaterialSettings,
  options?: {
    perObject?: MaterialProperties;
    isGhost?: boolean;
    opacity?: number;
    transparent?: boolean;
    /** Optional live texture to apply to the material's diffuse map slot */
    map?: THREE.Texture;
    /** Optional live texture maps for PBR channels beyond diffuse */
    roughnessMap?: THREE.Texture;
    metalnessMap?: THREE.Texture;
    normalMap?: THREE.Texture;
    alphaMap?: THREE.Texture;
    emissiveMap?: THREE.Texture;
    aoMap?: THREE.Texture;
  },
): THREE.MeshStandardMaterial {
  const isGhost = options?.isGhost ?? false;
  const perObj = options?.perObject;
  const transparent = options?.transparent ?? false;
  const opacity = options?.opacity ?? 1;
  const map = options?.map ?? null;
  // Alpha map makes the material transparent by definition
  const hasAlphaMap = !isGhost && !!options?.alphaMap;
  const usePhysical = !isGhost && needsPhysicalMaterial(perObj, settings);

  // --- Shared properties for both material types ---
  const baseProps: THREE.MeshStandardMaterialParameters = {
    color: isGhost ? new THREE.Color(0.6, 0.6, 0.8) : new THREE.Color(color),
    map: isGhost ? null : (map ?? null),
    roughnessMap: isGhost ? null : (options?.roughnessMap ?? null),
    metalnessMap: isGhost ? null : (options?.metalnessMap ?? null),
    normalMap: isGhost ? null : (options?.normalMap ?? null),
    alphaMap: isGhost ? null : (options?.alphaMap ?? null),
    emissiveMap: isGhost ? null : (options?.emissiveMap ?? null),
    aoMap: isGhost ? null : (options?.aoMap ?? null),
    metalness: isGhost
      ? settings.metalness
      : perObj && perObj.metalness >= 0
        ? perObj.metalness
        : settings.metalness,
    roughness: isGhost
      ? 0.9
      : perObj && perObj.roughness >= 0
        ? perObj.roughness
        : settings.roughness,
    envMapIntensity: isGhost ? 0.1 : settings.envMapIntensity,
    flatShading: settings.flatShading,
    side: transparent || hasAlphaMap ? THREE.DoubleSide : THREE.FrontSide,
    transparent: transparent || hasAlphaMap,
    opacity,
    depthWrite: !transparent && !hasAlphaMap,
  };

  // --- Emissive ---
  if (!isGhost && perObj && perObj.emissive[0] >= 0) {
    baseProps.emissive = new THREE.Color(
      perObj.emissive[0],
      perObj.emissive[1],
      perObj.emissive[2],
    );
  }
  if (!isGhost && perObj && perObj.emissiveIntensity >= 0) {
    baseProps.emissiveIntensity = perObj.emissiveIntensity;
  }
  if (
    !isGhost &&
    perObj &&
    perObj.emissive[0] >= 0 &&
    perObj.emissiveIntensity > 0
  ) {
    baseProps.toneMapped = false;
  }

  if (usePhysical) {
    const physProps: THREE.MeshPhysicalMaterialParameters = { ...baseProps };

    // Per-object physical properties (priority) -> scene-level fallback
    const v = (perObjVal: number, sceneVal: number) =>
      perObjVal >= 0 ? perObjVal : sceneVal;

    physProps.clearcoat = v(perObj?.clearcoat ?? -1, settings.clearcoat);
    physProps.clearcoatRoughness = v(
      perObj?.clearcoatRoughness ?? -1,
      settings.clearcoatRoughness,
    );
    physProps.sheen = v(perObj?.sheen ?? -1, settings.sheen);
    physProps.sheenRoughness = v(
      perObj?.sheenRoughness ?? -1,
      settings.sheenRoughness,
    );

    // Sheen color
    if (perObj && perObj.sheenColor[0] >= 0) {
      physProps.sheenColor = new THREE.Color(
        perObj.sheenColor[0],
        perObj.sheenColor[1],
        perObj.sheenColor[2],
      );
    } else {
      physProps.sheenColor = new THREE.Color(...settings.sheenColor);
    }

    physProps.iridescence = v(perObj?.iridescence ?? -1, settings.iridescence);
    physProps.iridescenceIOR = v(
      perObj?.iridescenceIOR ?? -1,
      settings.iridescenceIOR,
    );

    const transmissionVal = v(
      perObj?.transmission ?? -1,
      settings.transmission,
    );
    physProps.transmission = transmissionVal;
    physProps.ior = v(perObj?.ior ?? -1, settings.ior);
    physProps.thickness = v(perObj?.thickness ?? -1, settings.thickness);

    // Attenuation color
    if (perObj && perObj.attenuationColor[0] >= 0) {
      physProps.attenuationColor = new THREE.Color(
        perObj.attenuationColor[0],
        perObj.attenuationColor[1],
        perObj.attenuationColor[2],
      );
    } else {
      physProps.attenuationColor = new THREE.Color(
        ...settings.attenuationColor,
      );
    }
    physProps.attenuationDistance = v(
      perObj?.attenuationDistance ?? -1,
      settings.attenuationDistance,
    );

    // Transmission requires special handling for transparency
    if (transmissionVal > 0) {
      physProps.transparent = true;
      physProps.side = THREE.DoubleSide;
      physProps.depthWrite = false;
    }

    return new THREE.MeshPhysicalMaterial(physProps);
  }

  return new THREE.MeshStandardMaterial(baseProps);
}

/**
 * Update an existing material's properties in-place from scene-level settings.
 * This avoids creating a new material (and the associated GPU upload) every
 * time the user drags a slider.
 *
 * Per-object overrides are NOT touched here — they are baked in at creation
 * time and stay until the material is replaced.
 */
export function applySettingsToMaterial(
  material: THREE.MeshStandardMaterial,
  settings: MaterialSettings,
): void {
  material.metalness = settings.metalness;
  material.roughness = settings.roughness;
  material.envMapIntensity = settings.envMapIntensity;
  material.flatShading = settings.flatShading;

  if (material instanceof THREE.MeshPhysicalMaterial) {
    material.clearcoat = settings.clearcoat;
    material.clearcoatRoughness = settings.clearcoatRoughness;
    material.sheen = settings.sheen;
    material.sheenRoughness = settings.sheenRoughness;
    material.sheenColor = new THREE.Color(...settings.sheenColor);
    material.iridescence = settings.iridescence;
    material.iridescenceIOR = settings.iridescenceIOR;
    material.transmission = settings.transmission;
    material.ior = settings.ior;
    material.thickness = settings.thickness;
    material.attenuationColor = new THREE.Color(...settings.attenuationColor);
    material.attenuationDistance = settings.attenuationDistance;
  }

  material.needsUpdate = true;
}

/**
 * Safely dispose a material and any associated textures / maps.
 */
export function disposeMaterial(material: THREE.Material): void {
  // Dispose any maps the material holds (standard set)
  const std = material as THREE.MeshStandardMaterial;
  std.map?.dispose();
  std.lightMap?.dispose();
  std.bumpMap?.dispose();
  std.normalMap?.dispose();
  std.displacementMap?.dispose();
  std.roughnessMap?.dispose();
  std.metalnessMap?.dispose();
  std.alphaMap?.dispose();
  std.emissiveMap?.dispose();
  std.envMap?.dispose();
  std.aoMap?.dispose();

  // Physical material maps
  const phys = material as THREE.MeshPhysicalMaterial;
  phys.clearcoatMap?.dispose();
  phys.clearcoatRoughnessMap?.dispose();
  phys.clearcoatNormalMap?.dispose();
  phys.sheenColorMap?.dispose();
  phys.sheenRoughnessMap?.dispose();
  phys.iridescenceMap?.dispose();
  phys.iridescenceThicknessMap?.dispose();
  phys.transmissionMap?.dispose();
  phys.thicknessMap?.dispose();

  material.dispose();
}

/**
 * Safely dispose a BufferGeometry, freeing GPU vertex/index buffers.
 */
export function disposeGeometry(geometry: THREE.BufferGeometry): void {
  geometry.dispose();
}

/**
 * Create a THREE.ShaderMaterial from GLSL shader strings and initial uniform values.
 *
 * The `color` argument is automatically injected as a `uColor` uniform (vec3)
 * so shader scripts can tint or blend using the color() value from OpenSCAD.
 *
 * `initialUniforms` is a plain serialized record from the shader worker
 * (values are numbers or arrays, never Three.js objects). They are wrapped in
 * { value: ... } form before being passed to ShaderMaterial.
 *
 * Standard Three.js built-in vertex attributes (position, normal, uv) and
 * matrices (modelViewMatrix, projectionMatrix, normalMatrix, etc.) are
 * available in the GLSL without any manual declaration.
 */
export function createShaderMaterial(
  vertexShader: string,
  fragmentShader: string,
  initialUniforms: Record<string, unknown>,
  color: THREE.Color,
  options?: {
    transparent?: boolean;
    opacity?: number;
  },
): THREE.ShaderMaterial {
  // Build the uniforms map: inject uColor, then layer in the script's values
  const uniforms: Record<string, { value: unknown }> = {
    uColor: { value: color.clone() },
  };

  for (const [key, value] of Object.entries(initialUniforms)) {
    uniforms[key] = { value };
  }

  const transparent = options?.transparent ?? false;
  const opacity = options?.opacity ?? 1;

  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms,
    transparent,
    opacity,
    side: transparent ? THREE.DoubleSide : THREE.FrontSide,
    depthWrite: !transparent,
  });
}
